import { getDefaultRegistry } from "@rjsf/core";
import { useEffect, useRef } from "react";
import type { WidgetProps } from "@rjsf/utils";
import { useLocale } from "../../i18n/LocaleProvider";

const Checkboxes = getDefaultRegistry().widgets.CheckboxesWidget!;

/**
 * Every form's `"ui:widget": "checkboxes"`: the native checkbox widget, its accessible labels
 * kept, plus select-all and clear-all. `ui:options.collapsible` folds the list behind a summary.
 */
export function CategoryCheckboxes(props: WidgetProps) {
  const { t } = useLocale();
  const options = props.options.enumOptions ?? [];
  const selected = Array.isArray(props.value) ? props.value : [];
  const locked = props.options.enumDisabled ?? [];
  const editable = options.filter(option => !locked.includes(option.value));
  const count = options.filter(option => selected.includes(option.value)).length;
  const disabled = props.disabled || props.readonly;
  const collapsible = props.options.collapsible === true;
  const disclosure = useRef<HTMLDetailsElement>(null);
  const hasErrors = !!props.rawErrors?.length;
  useEffect(() => {
    if (hasErrors && disclosure.current) disclosure.current.open = true;
  }, [hasErrors]);
  const summary = t("workSettings.checklist.count", { count, total: options.length });
  const choices = <>
    <div className="work-settings-category-actions">
      {!collapsible && <span aria-live="polite">{summary}</span>}
      <button type="button" disabled={disabled || editable.every(option => selected.includes(option.value))}
        onClick={() => props.onChange(options.filter(option => !locked.includes(option.value) || selected.includes(option.value)).map(option => option.value))}>
        {t("workSettings.checklist.all")}
      </button>
      <button type="button" disabled={disabled || !editable.some(option => selected.includes(option.value))}
        onClick={() => props.onChange(selected.filter(value => locked.includes(value)))}>
        {t("workSettings.checklist.none")}
      </button>
    </div>
    <Checkboxes {...props} />
  </>;
  if (!collapsible) return choices;
  return <details ref={disclosure} className="work-settings-category-disclosure">
    <summary className="work-settings-category-toggle">
      <span aria-live="polite">{summary}</span>
      <span className="work-settings-category-toggle-action">
        {t("workSettings.checklist.choose")}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
      </span>
    </summary>
    <div className="work-settings-category-choices">{choices}</div>
  </details>;
}
